// Shared domain types for the training app. Bundled drill data, the plan
// generator, the Worker API, and the UI all import from here.

export type Position = "forward" | "goalkeeper";
export type DrillPosition = Position | "all";

export type Block =
  | "warmup"
  | "technical" // shared foundations (both positions)
  | "position" // position + focus specific
  | "challenge" // scored, fun
  | "cooldown";

export const BLOCK_ORDER: Block[] = [
  "warmup",
  "technical",
  "position",
  "challenge",
  "cooldown",
];

// What a drill needs set up. GK drills often need a competent server.
export type SetupNeed = "solo" | "server" | "partner";
export type Space = "small" | "half" | "full";

// Focus areas per position. Kept as string ids so drills tag against them.
export const FORWARD_SKILLS = [
  "finishing",
  "first-touch",
  "dribbling",
  "passing",
  "movement",
  "weak-foot",
] as const;

export const GOALKEEPER_SKILLS = [
  "shot-stopping",
  "handling",
  "footwork",
  "distribution",
  "crosses",
  "one-v-one",
] as const;

export type ForwardSkill = (typeof FORWARD_SKILLS)[number];
export type GoalkeeperSkill = (typeof GOALKEEPER_SKILLS)[number];
export type Skill = ForwardSkill | GoalkeeperSkill | "conditioning" | "agility";

export const SKILL_LABELS: Record<string, string> = {
  finishing: "Finishing",
  "first-touch": "First touch",
  dribbling: "Dribbling & 1v1",
  passing: "Passing & combination",
  movement: "Off-ball movement",
  "weak-foot": "Weak foot",
  "shot-stopping": "Shot-stopping / reflexes",
  handling: "Handling & catching",
  footwork: "Footwork & positioning",
  distribution: "Distribution",
  crosses: "High balls & crosses",
  "one-v-one": "1v1 & breakaways",
  conditioning: "Conditioning",
  agility: "Agility",
};

export interface DrillVideo {
  youtubeId: string; // youtube-nocookie embed id
  startSec?: number;
  title: string;
  // Every candidate ships false. Flipped to true only after Xavier approves
  // that the clip shows THIS drill. Never present an unverified video as vetted.
  verified: boolean;
}

export interface DrillVariants {
  easier?: string;
  harder?: string;
}

export interface Benchmark {
  metric: string; // machine key, e.g. "hits_of_10"
  label: string; // human, e.g. "Targets hit out of 10"
  unit?: string;
}

export interface Drill {
  id: string;
  name: string;
  position: DrillPosition[];
  skills: Skill[];
  block: Block;
  difficulty: 1 | 2 | 3 | 4 | 5;
  minutes: number; // nominal duration incl. rest
  equipment: string[]; // e.g. ["ball","cones","goal"]
  setupNeed: SetupNeed;
  space: Space;
  setup: string;
  steps: string[];
  cues: string[];
  variants: DrillVariants;
  video: DrillVideo;
  // Anchor drills carry a benchmark and are kept stable across sessions so
  // progress is comparable (measure improvement, not minutes trained).
  anchor?: boolean;
  benchmark?: Benchmark;
}

export type DrillLibrary = {
  version: number;
  drills: Drill[];
};

// ---- Profiles -------------------------------------------------------------

export interface Profile {
  id: string; // "nathan" | "wyatt" (but open for future kids)
  name: string;
  age: number;
  position: Position;
  focusAreas: Skill[];
  updatedAt: string; // ISO
}

// ---- Session generation ---------------------------------------------------

export type HelperMode = "solo" | "helper" | "together";

export interface Constraints {
  durationMin: 30 | 45 | 60;
  helper: HelperMode;
  equipment: string[]; // what's on hand right now
  space: Space;
}

export interface PlannedDrill {
  drill: Drill;
  block: Block;
  allocatedMin: number;
}

export interface SessionPlan {
  id: string; // uuid
  profileId: string;
  date: string; // ISO date (yyyy-mm-dd)
  durationMin: number;
  constraints: Constraints;
  drills: PlannedDrill[];
  libraryVersion: number;
}

export type SessionStatus = "generated" | "completed";

export interface SessionResult {
  drillId: string;
  metric: string;
  value: number;
}

export interface SessionRecord {
  id: string;
  profileId: string;
  date: string;
  durationMin: number;
  plan: SessionPlan;
  status: SessionStatus;
  completedAt?: string;
  perceivedDifficulty?: 1 | 2 | 3 | 4 | 5;
  notes?: string;
  results?: SessionResult[];
}
